import {
    IAirport,
    IField,
    ValidationTypes,
    airports,
    getObjectById,
} from '@eventr/common'
import {
    ColoursObj,
    Help,
    NativeUtils,
    Typography,
    useValidatorContext,
} from '@eventr/common-storybook'
import { Autocomplete, Paper, TextField, Tooltip } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { useEditSectionContext } from '../editSectionProvider'
interface ITextInputFieldProps {
    field: IField
    _id?: string
}

export const AirportSelector = ({ field, _id }: ITextInputFieldProps) => {
    const { getValue, updateValues, values } = useEditSectionContext()
    const { getFieldById } = useBoundStore(
        useShallow((s) => {
            return {
                getFieldById: s.getFieldById,
            }
        })
    )
    const [isError, setIsError] = useState<boolean>(false)
    const [textError, setTextError] = useState<string>('')
    const { isFieldValid } = useValidatorContext()

    const [state, setState] = useState<IAirport>({
        label: '',
        id: '',
        obj: { municipality: '' },
    })

    const [listOnOpen] = useState(false)
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = React.useState('')

    useEffect(() => {
        const initFieldValue = getValue(field, _id)
        // const obj = getObjectById(_id, values)

        if (initFieldValue) {
            let x = airports.find((a) => a.label === initFieldValue.label)
            if (x) {
                setState(initFieldValue)
            } else {
                setState({
                    label: '',
                    id: '',
                    obj: { municipality: '' },
                })
            }
        }
    }, [field, _id, getValue])

    useEffect(() => {
        const valid = isFieldValid(field, _id, state?.label)
        if ((valid !== true) !== isError) {
            setIsError(valid !== true)
        }
        if (valid !== true) {
            setTextError(valid as string)
        } else {
            setTextError('')
        }
    }, [state, field, _id, isFieldValid, setIsError, isError, setTextError])

    const isRequired = useMemo(() => {
        return field.type_properties?.validations?.some(
            (v) => v.type === ValidationTypes.required
        )
    }, [field.type_properties?.validations])

    const handleChanges = (value: IAirport) => {
        setState(value)
        updateValues({
            value: value,
            field: field,
            _id: _id,
        })
        const obj = getObjectById(_id, values)

        if (String(field.id) === '237') {
            updateValues({
                value: value?.id ? value.id : '',
                field: getFieldById(203),
                _id: _id,
            })

            if (obj?.departure._id) {
                updateValues({
                    value: value?.label ? value.label.split(' (')[0] : '',
                    field: getFieldById(222),
                    _id: obj.departure._id,
                })
            }
        } else {
            updateValues({
                value: value?.id ? value.id : '',
                field: getFieldById(204),
                _id: _id,
            })
            if (obj?.arrival._id) {
                updateValues({
                    value: value?.label ? value.label.split(' (')[0] : '',
                    field: getFieldById(223),
                    _id: obj.arrival._id,
                })
            }
        }
    }

    const filterOptions = (options: any, { inputValue }: any) => {
        let x = options.filter((option: any) => {
            return (
                option.label.toLowerCase() +
                option?.obj?.municipality.toLowerCase()
            ).includes(inputValue.toLowerCase())
        })
        //can reduce this filtering down if its very slow
        //sort by size and then if it matches the id of the airport
        x.sort((a: any, b: any) => {
            if (a.obj.size < b.obj.size) {
                return -1
            } else {
                return 1
            }
        })
        x.sort((a: any, b: any) => {
            if (a.label.toLowerCase().startsWith(inputValue.toLowerCase())) {
                return -1
            } else {
                return 1
            }
        })
        x.sort((a: any, b: any) => {
            if (a.id.toLowerCase().includes(inputValue.toLowerCase())) {
                if (b.id.toLowerCase().includes(inputValue.toLowerCase())) {
                    if (
                        a.id.toLowerCase().startsWith(inputValue.toLowerCase())
                    ) {
                        return -1
                    } else {
                        return 1
                    }
                }
                return -1
            } else {
                return 1
            }
        })

        return x
    }

    return (
        <div>
            <Autocomplete
                id={`${field.field_name}-selector`}
                options={airports}
                open={open}
                onOpen={() => {
                    if (listOnOpen) {
                        setOpen(true)
                    } else {
                        if (inputValue !== '') {
                            setOpen(true)
                        }
                    }
                }}
                onClose={() => {
                    setOpen(false)
                }}
                disableListWrap
                selectOnFocus
                placeholder="Search for an airport"
                value={state as IAirport}
                filterOptions={filterOptions}
                onInputChange={(_, value) => {
                    setInputValue(value)
                    if (!value) {
                        setOpen(false)
                    }
                }}
                inputValue={inputValue}
                isOptionEqualToValue={(option, value) => {
                    if (option.label !== null && value.label !== null) {
                        return option.label === value.label
                    }
                }}
                getOptionLabel={(option) => {
                    return option.label
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        error={isError}
                        onClick={(e) => {
                            NativeUtils.hapticFeedback('light')
                        }}
                        sx={{
                            Label: {
                                transform: 'translateY(-18px)',
                            },
                        }}
                        style={{
                            marginTop: '8px',
                        }}
                        helperText={textError}
                        label={
                            field.field_name && (
                                <div style={{ display: 'flex' }}>
                                    <Typography variant="bodySubtext">
                                        {i18n.t(
                                            `edit-itinerary.fields.${field.id}`
                                        )}
                                        {isRequired && ' *'}
                                    </Typography>
                                    {textError && (
                                        <Tooltip
                                            title={textError}
                                            placement="right-start"
                                            arrow
                                        >
                                            <div
                                                style={{
                                                    height: 'fit-content',
                                                    padding: '0 0 0 8px',
                                                }}
                                            >
                                                <Help height={16} width={16} />
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            )
                        }
                    />
                )}
                onChange={(_, newValue: IAirport) => {
                    handleChanges(newValue)
                }}
                PaperComponent={(props) => (
                    <Paper
                        {...props}
                        style={{
                            background: ColoursObj.backgrounds.secondary,
                            color: ColoursObj.core.primary,
                        }}
                    />
                )}
            />
        </div>
    )
}
